import React from 'react';
import { Badge } from '../../ui/badge';
import { ArrowUp, ArrowDown, Clock, User, Wrench } from 'lucide-react';
import { Quote } from '../../../models/workItem';
import { cn } from '../../../lib/utils';

interface QuoteSummaryProps {
  quote: Quote;
}

const QuoteSummary: React.FC<QuoteSummaryProps> = ({ quote }) => {
  const fxerEstimate = quote.analysis.lineItems.reduce(
    (acc, lineItem) => {
      const laborCosts = lineItem.analysis.subTasks.reduce(
        (subAcc, subTask) => {
          const subLaborLow = subTask.options.reduce(
            (optAcc, option) =>
              optAcc + option.expectedLaborHoursLow * option.laborCostLowPerHour,
            0
          );
          const subLaborHigh = subTask.options.reduce(
            (optAcc, option) =>
              optAcc + option.expectedLaborHoursHigh * option.laborCostHighPerHour,
            0
          );
          return {
            low: subAcc.low + subLaborLow,
            high: subAcc.high + subLaborHigh,
          };
        },
        { low: 0, high: 0 }
      );

      const materialCosts = lineItem.analysis.subTasks.reduce(
        (subAcc, subTask) => {
          const subMaterialLow = subTask.options.reduce(
            (optAcc, option) =>
              optAcc +
              option.materials.reduce(
                (matAcc, material) =>
                  matAcc + material.lowCostPerUnit * material.quantityLow,
                0
              ),
            0
          );
          const subMaterialHigh = subTask.options.reduce(
            (optAcc, option) =>
              optAcc +
              option.materials.reduce(
                (matAcc, material) =>
                  matAcc + material.highCostPerUnit * material.quantityHigh,
                0
              ),
            0
          );
          return {
            low: subAcc.low + subMaterialLow,
            high: subAcc.high + subMaterialHigh,
          };
        },
        { low: 0, high: 0 }
      );

      const laborLow = laborCosts.low;
      const laborHigh = laborCosts.high;
      const materialLow = materialCosts.low;
      const materialHigh = materialCosts.high;

      return {
        laborLow: acc.laborLow + laborLow,
        laborHigh: acc.laborHigh + laborHigh,
        materialLow: acc.materialLow + materialLow,
        materialHigh: acc.materialHigh + materialHigh,
        totalHoursLow: acc.totalHoursLow + lineItem.analysis.subTasks.reduce(
          (sum, subTask) =>
            sum +
            subTask.options.reduce(
              (optSum, option) => optSum + option.expectedLaborHoursLow,
              0
            ),
          0
        ),
        totalHoursHigh: acc.totalHoursHigh + lineItem.analysis.subTasks.reduce(
          (sum, subTask) =>
            sum +
            subTask.options.reduce(
              (optSum, option) => optSum + option.expectedLaborHoursHigh,
              0
            ),
          0
        ),
        totalLow: acc.totalLow + laborLow + materialLow,
        totalHigh: acc.totalHigh + laborHigh + materialHigh,
      };
    },
    {
      laborLow: 0,
      laborHigh: 0,
      materialLow: 0,
      materialHigh: 0,
      totalHoursLow: 0,
      totalHoursHigh: 0,
      totalLow: 0,
      totalHigh: 0,
    }
  );

  const formatDifference = (expected: [number, number], given: number) => {
    if (given === 0) return { difference: 'N/A', status: 'text-slate-500', label: 'Not Given', arrow: null };

    const [low, high] = expected;

    let difference = 0;
    let status = 'text-green-600'; // Green for In Range
    let label = 'In Range';
    let arrow = null;
    let bgColor = 'bg-green-50';
    let borderColor = 'border-green-200';

    if (given > high) {
      difference = ((given - high) / high) * 100;
      status = 'text-red-600'; // Red for Over Range
      label = 'Over Estimate';
      arrow = <ArrowUp className="h-4 w-4" />;
      bgColor = 'bg-red-50';
      borderColor = 'border-red-200';
    } else if (given < low) {
      difference = ((low - given) / low) * 100;
      status = 'text-amber-600'; // Yellow for Under Range
      label = 'Under Estimate';
      arrow = <ArrowDown className="h-4 w-4" />;
      bgColor = 'bg-amber-50';
      borderColor = 'border-amber-200';
    }

    return {
      difference: difference.toFixed(1),
      status,
      label,
      arrow,
      bgColor,
      borderColor
    };
  };

  const totalDifference = formatDifference(
    [fxerEstimate.totalLow, fxerEstimate.totalHigh],
    quote.analysis.quoteTotalCost
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Fxer Estimate */}
      <div className="space-y-3">
        <h4 className="font-bold text-base">Fxer Estimate</h4>
        
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-600 flex-shrink-0" />
            <span>{fxerEstimate.totalHoursLow} - {fxerEstimate.totalHoursHigh} hours</span>
          </div>
          
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
            <span>Labor: ${fxerEstimate.laborLow.toLocaleString()} - ${fxerEstimate.laborHigh.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-slate-600 flex-shrink-0" />
            <span>Material: ${fxerEstimate.materialLow.toLocaleString()} - ${fxerEstimate.materialHigh.toLocaleString()}</span>
          </div>
        </div>
        
        <div className="font-bold mt-2 text-lg">
          Total: ${fxerEstimate.totalLow.toLocaleString()} - ${fxerEstimate.totalHigh.toLocaleString()}
        </div>
      </div>
      
      {/* Contractor Quote */}
      <div className="space-y-3">
        <h4 className="font-bold text-base">Contractor Quote</h4>
        
        <div className="text-2xl font-semibold">
          ${quote.analysis.quoteTotalCost?.toLocaleString() || 'Not Given'}
        </div>
        
        {totalDifference.difference !== 'N/A' && (
          <Badge 
            variant="outline" 
            className={cn(
              "flex items-center gap-1 text-sm", 
              totalDifference.bgColor, 
              totalDifference.status, 
              totalDifference.borderColor
            )}
          >
            {totalDifference.arrow}
            <span>
              {totalDifference.difference === "0.0" ? "" : totalDifference.difference + "%" } {totalDifference.label}
            </span>
          </Badge>
        )}
      </div>
    </div>
  );
};

export default QuoteSummary;
